import React, { useEffect, useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { get, find, isEmpty } from 'lodash';
import { Button as MaterialButton, Grid } from '@material-ui/core';
import { ThemeProvider, createTheme } from '@material-ui/core/styles';

import DefaultMaterialTable from 'common/util/materialTable';
import { optionsBuilder } from 'common/util/materialTable/options';
import { LoadingStatus } from 'components/common/LoadingStatus/LoadingStatus';
import { customTheme } from 'common/util/materialTable/theme';
import { Conditional } from 'common/components/layout';
import { Button, BUTTON } from 'components/common/Button/Button';
import EmailConfig from './EmailConfig';
import {
    loadSelectedReport,
    setReportValue,
    fetchCustomizeColumns,
    fetchReportData,
    resetReportData,
    setCustomizeColumns,
    resetCustomizeColumns,
    resetSelectedReport,
    submitReport,
} from '../actions/reportActions';
import {
    getCanSubmitReport,
    getReportData,
    getReportOptions,
    getSelectedReport,
    getSubmitData,
} from '../selectors/reportSelectors';
import DialogDownloadReport from '../components/DialogDownloadReport';
import PopoverCustomizeColumns from '../components/PopoverCustomizeColumns';
import { DEFAULT_LIMIT, DEFAULT_OFFSET, reportLinks, reportTypes } from '../utils/constants';
import { buildReportQuery, renderReportColumns, customLocalization, isDynamicTableEnabled, renderColumns } from '../utils/reportUtils';
import styles from './ReportContent.scss';
import ReportTable from '../components/ReportTable';

const tableTheme = customTheme({
    MuiPaper: {
        root: {
            width: '99%',
            margin: '0 auto',
            boxShadow: 'none !important',

            '& > div > div > div': {
                overflowY: 'hidden !important',
            },
        },
    },
    MuiTable: {
        root: {
            width: '100%',
            margin: '0 auto',
            borderBottom: 'none !important'
        },
    },

    MuiTableSortLabel: {
        root: {
            fontSize: '16px',
            whiteSpace: 'nowrap',
        },
    },

    MuiInput: {
        root: {
            color: '#28282E !important',
            fontSize: '14px',
            fontWeight: '600',
            opacity: '0.6',
        },
    },

    MuiTableCell: {
        root: {
            fontSize: '14px !important',
            borderBottom: 'none',
            wordBreak: 'break-word !important',
            whiteSpace: 'normal !important'
        },

        head: {
            fontSize: '16px !important',
            minWidth: '200px',
        },
    },

    MuiTableRow: {
        root: {
            wordWrap: 'break-word',
        }
    }
});

const popoverTheme = createTheme({
    overrides: {
        MuiPopover: {
            paper: {
                width: 'fit-content !important',
            },
        },
    },
});

const tableOptions = optionsBuilder({
    search: false,
    filtering: true,
    toolbar: false,
});

const DEFAULT_REPORT_FILTERS = {
    limit: DEFAULT_LIMIT,
    offset: DEFAULT_OFFSET,
    sortField: '',
    sortType: '',
    filters: [],
};

export function ReportContent({
    reportType,
    fetchReportData,
    fetchCustomizeColumns,
    setCustomizeColumns,
    resetReportData,
    resetCustomizeColumns,
    isLoading,
    isLoadingCustomizeColumn,
    totalCount,
    hasNextPage,
    reportData,
    reportDownloadData,
    columns,
    customizeColumns,
    canSubmitReport,
    submitReport,
    submitData,
    reportOptions,
    setReportValue,
    loadSelectedReport,
    resetSelectedReport,
    institutionId,
}) {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [anchorCustomizeColumns, setAnchorCustomizeColumns] = useState(null);
    const [emailConfigOpen, setEmailConfigOpen] = useState(false);
    const [reportFilters, setReportFilters] = useState({ ...DEFAULT_REPORT_FILTERS });
    const [isInitialLoading, setIsInitialLoading] = useState(true);

    const toggleDialog = () => setDialogOpen(!dialogOpen);

    const resetFilters = () => {
        setReportFilters({ ...DEFAULT_REPORT_FILTERS });
    };

    useEffect(() => {
        if (!isDynamicTableEnabled[reportType] || reportType === reportTypes.billableHistory) {
            const args = {
                institutionId,
            };
            fetchReportData(buildReportQuery(reportType, args), reportType);
        }
        fetchCustomizeColumns(reportType, institutionId);
        setReportValue('institutionId', institutionId);
        loadSelectedReport(reportType);
        goBackReport();

        return () => {
            resetReportData(reportType);
            resetCustomizeColumns(reportType);
            resetSelectedReport(reportType);
            resetFilters();
        };
    }, [reportType]);

    useEffect(() => {
        if (isDynamicTableEnabled[reportType] && reportType != reportTypes.billableHistory) {
            const args = {
                ...reportFilters,
                institutionId,
            };
            const reportQuery = buildReportQuery(reportType, args);
            fetchReportData(reportQuery, reportType);
        }
    }, [reportFilters]);

    const downloadReport = () => {
        if (canSubmitReport) {
            const modifiedSubmitData = { ...submitData };
            if (!modifiedSubmitData.ststatusesate) {
                modifiedSubmitData.statuses = ['active', 'archived', 'new'];
            }
            submitReport(modifiedSubmitData);
        }
    };

    const handleCustomizeColumnsClick = event => {
        if (isEmpty(customizeColumns)) return;
        setAnchorCustomizeColumns(event.currentTarget);
    };

    const handleCustomizeColumnsClose = () => {
        setAnchorCustomizeColumns(null);
    };

    const handleCustomizeColumnsSave = columnNames => {
        setCustomizeColumns(reportType, columnNames, institutionId);
    };

    const handleEmailConfigClick = () => {
        setEmailConfigOpen(true);
    };

    const goBackReport = () => {
        setEmailConfigOpen(false);
    };

    const onChangeReportFilters = data => {
        setReportFilters({
            ...reportFilters,
            ...data,
        });
    };

    const localization = useMemo(() => customLocalization(isLoading), [isLoading]);

    const reportLabel = [
        reportTypes.userDocumentAuditTrail,
        reportTypes.task,
        reportTypes.document,
        reportTypes.billableHistory
    ].includes(reportType)
        ? 'Send Report'
        : 'Download Report';
    if ((isLoading || isLoadingCustomizeColumn) && isInitialLoading) {
        return <LoadingStatus actionLabel={'loading'} itemName={''} loading={true} filterQuery={false} />;
    }

    if ((isLoading && isLoadingCustomizeColumn) && !isInitialLoading) {
        setIsInitialLoading(true);
    }

    if (!(isLoading && isLoadingCustomizeColumn) && isInitialLoading) {
        setIsInitialLoading(false);
    }

    if (emailConfigOpen) {
        return <EmailConfig reportType={reportType} goBackReport={goBackReport} />;
    }

    return (
        <Grid className={reportType} container data-cy="ReportContent">
            <Grid
                className={styles.buttonGroups}
                container
                direction="row"
                alignItems="center"
                justifyContent="space-between"
            >
                <Button bgcolor={BUTTON.BGCOLOR.BLUE} color={BUTTON.COLOR.WHITE} onClick={toggleDialog} data-cy="ReportContent-toggleDialog">
                    {reportLabel}
                </Button>
                <div className={styles.rightButtons} data-cy="ReportContent-div">
                    <MaterialButton onClick={handleCustomizeColumnsClick}>Customize Columns</MaterialButton>
                    <MaterialButton onClick={handleEmailConfigClick}>Email Configuration</MaterialButton>
                </div>
            </Grid>
            <DialogDownloadReport
                reportLabel={reportLabel}
                reportOptions={reportOptions}
                reportData={reportDownloadData}
                canSubmitReport={canSubmitReport}
                loadReport={downloadReport}
                dialogOpen={dialogOpen}
                toggleDialog={toggleDialog}
            />

            <ThemeProvider theme={popoverTheme}>
                <PopoverCustomizeColumns
                    reportType={reportType}
                    anchorEl={anchorCustomizeColumns}
                    customizeColumns={customizeColumns}
                    onClose={handleCustomizeColumnsClose}
                    onSave={handleCustomizeColumnsSave}
                />
            </ThemeProvider>
            <Grid container>
                <Conditional condition={isDynamicTableEnabled[reportType] && reportType !== reportTypes.billableHistory}>
                    <ReportTable
                        reportType={reportType}
                        tableTheme={tableTheme}
                        totalCount={totalCount}
                        hasNextPage={hasNextPage}
                        columns={columns}
                        reportData={reportData}
                        reportFilters={reportFilters}
                        onChangeReportFilters={onChangeReportFilters}
                        localization={localization}
                    />
                </Conditional>
                <Conditional condition={!isDynamicTableEnabled[reportType] || reportType === reportTypes.billableHistory} >
                    <DefaultMaterialTable
                        key={reportType}
                        theme={tableTheme}
                        options={tableOptions}
                        columns={reportType === reportTypes.billableHistory ? renderColumns(reportType, columns, reportFilters) :columns}
                        data={reportData}
                        localization={localization}
                    />
                </Conditional>
            </Grid>
        </Grid>
    );
}

export default connect(
    (state, props) => {
        const { pathname } = props.location;
        const reportLink = find(reportLinks, { to: pathname });
        const reportType = reportLink ? reportLink.type : '';
        const {
            report = {},
            me: { institution },
        } = state;

        const isLoading = get(report, `${reportType}.isLoading`, false);
        const isLoadingCustomizeColumn = get(report, `${reportType}.isLoadingCustomizeColumn`, false);
        const reportData = get(report, `${reportType}.data`, []);
        const customizeColumns = get(report, `${reportType}.customizeColumns`, []);
        const totalCount = get(report, `${reportType}.count`, 7);
        const hasNextPage = get(report, `${reportType}.hasNextPage`, false);
        const reportDownloadData = getReportData(state);
        const institutionId = institution.id;
        const selectedReport = getSelectedReport(state);
        const reportOptions = getReportOptions(state);
        const submitData = getSubmitData(state);
        const canSubmitReport = getCanSubmitReport(state);
        const columns = renderReportColumns(reportType, reportData, customizeColumns);
        return {
            reportType,
            isLoading,
            isLoadingCustomizeColumn,
            reportData,
            reportDownloadData,
            totalCount,
            hasNextPage,
            columns,
            customizeColumns,
            institutionId,
            selectedReport,
            reportOptions,
            submitData,
            canSubmitReport,
        };
    },
    {
        loadSelectedReport,
        setReportValue,
        fetchReportData,
        fetchCustomizeColumns,
        setCustomizeColumns,
        resetReportData,
        resetCustomizeColumns,
        resetSelectedReport,
        submitReport,
    },
)(ReportContent);
